import classNames from 'classnames';
import fromExponential from 'from-exponential';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useEffectOnce, useInterval } from 'usehooks-ts';
import { v4 } from 'uuid';

import { SvgIconUrl } from 'common/components';

import {
  checkAndFormatNumericInput,
  decimalAddition,
  decimalMinus,
  disallowNonNumericSymbols,
  localizeNumber,
} from 'common/helpers';
import { useBowser } from 'common/hooks';
import svgIcons from 'common/svgIcons';

import { detectPrecision } from 'utils/detectPrecision';
import { truncateString } from 'utils/truncateNumber';

import './index.css';

export type LocalizedNumberInputFormValues<F> = FieldValues & Partial<F>;

type LocalizedNumberInputValidateFunction<T> = (value: T) => boolean | string;
type LocalizedNumberInputValidateObject<T> = Record<string, (value: T, formValues: Record<string, FieldValues>) => boolean | string>;

type LocalizedNumberInputValidate<T> = LocalizedNumberInputValidateFunction<T> | LocalizedNumberInputValidateObject<T>;

export interface LocalizeNumberInputProps<T = string | number | undefined> {
  name: string;
  value: T | null;
  defaultValue?: T | null;
  digits: number;
  label?: React.ReactNode;
  error?: string;
  deps?: string[];
  step?: number;
  stepUp?: number;
  stepDown?: number;
  validate?: LocalizedNumberInputValidate<T>;
  currency?: string | React.ReactNode;
  disabled?: boolean;
  'data-test'?: string;
  showPrefix?: boolean;
  showSteps?: boolean;
  allowLocalize?: boolean;
  isValid?: boolean;
  autoFocus?: boolean;
  max?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  onChange?: (value: string) => void;
  options?: React.ReactNode;
  className?: string;
  isActive?: boolean;
}

const DEFAULT_CONTROL_SPEED = 200;
const MINIMAL_CONTROL_SPEED = 100;
const DIGITS_DECREMENT_INTERVAL = 1000;

const defaultControlConfig = {
  speed: DEFAULT_CONTROL_SPEED,
  digits: 0,
  active: false,
};

const _LocalizedNumberInput = React.forwardRef<HTMLDivElement, LocalizeNumberInputProps>(
  (
    {
      currency,
      disabled,
      label,
      digits = 0,
      onFocus,
      onBlur,
      onChange,
      value = '',
      defaultValue = '',
      error,
      'data-test': dataTest = 'field',
      className = '',
      name,
      showPrefix = false,
      showSteps = false,
      allowLocalize = true,
      isValid = true,
      validate,
      options,
      deps,
      max,
      step,
      stepUp,
      stepDown,
      autoFocus,
      isActive,
    },
    ref,
  ) => {
    const form = useFormContext();
    const { isMobile } = useBowser();
    const [active, toggle] = React.useState(false);
    const [initial] = React.useState(value || 0);

    const [isMinusPressed, setIsMinusPressed] = React.useState(false);
    const [isPlusPressed, setIsPlusPressed] = React.useState(false);

    const [controlConfig, _updateControlConfig] = React.useState({
      ...defaultControlConfig,
      digits,
    });

    const onKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => disallowNonNumericSymbols(event, digits),
      [digits],
    );

    const _step: number = useMemo(
      () =>
        step ||
        parseFloat(
          parseFloat(initial.toString())
            .toFixed(controlConfig.digits)
            .split('')
            .map((symbol, index, array) => {
              if (Number.isNaN(+symbol)) return symbol;
              if (index + 1 !== array.length) return 0;
              return 1;
            })
            .join(''),
        ),
      [step, initial, controlConfig.digits],
    );

    const minDigits = useMemo(() => {
      if (max) return detectPrecision(max);

      return 0;
    }, [max]);

    const updateControlConfig = useCallback(
      (data: Partial<typeof controlConfig>) => {
        _updateControlConfig({
          active: data.active ?? controlConfig.active,
          speed: data.speed ?? controlConfig.speed,
          digits: data.digits ?? controlConfig.digits,
        });
      },
      [controlConfig.active, controlConfig.speed, controlConfig.digits],
    );

    const onDecrementDigits = useCallback(() => {
      const _digits = controlConfig.digits - 1;

      if (_digits < minDigits) {
        updateControlConfig({ ...controlConfig, active: true });
        return;
      }

      updateControlConfig({
        ...controlConfig,
        digits: _digits,
        speed: MINIMAL_CONTROL_SPEED,
        active: true,
      });
    }, [controlConfig, minDigits, updateControlConfig]);

    const isStepField = useMemo(() => showSteps || step || stepUp || stepDown, [showSteps, step, stepDown, stepUp]);

    const fieldClassName = useMemo(
      () =>
        classNames('field-localized', {
          'field-localized--stepper': isStepField,
          'field-localized--stepper-with-options': isStepField && options,
          'field-localized--stepper-with-currency': isStepField && currency,
          'field-localized--invalid': !!error || !isValid,
          'field-localized--filled': !!value,
          'field-localized--active': active || isActive,
          'field-localized--calculated': !!value && showPrefix,
          [className]: className,
        }),
      [isStepField, options, currency, error, isValid, value, active, isActive, showPrefix, className],
    );
    const inputClassName = useMemo(
      () =>
        classNames('field-localized__input', {
          'field-localized__input--invalid': !!error || !isValid,
        }),
      [error, isValid],
    );
    const labelClassName = useMemo(
      () =>
        classNames('field-localized__label', {
          'field-localized__label--active': active,
          'field-localized__label--filled': !!value,
          'field-localized__label--empty': !value && active,
        }),
      [active, value],
    );

    const localizedFieldValue = useMemo(
      () =>
        localizeNumber(value, {
          minDigits: controlConfig.active ? controlConfig.digits : 0,
          maxDigits: controlConfig.digits,
        }),
      [controlConfig.active, controlConfig.digits, value],
    );

    const fieldValue = useMemo(
      () =>
        fromExponential(
          !value ? '' : active || !allowLocalize ? truncateString(value, controlConfig.digits) : localizedFieldValue,
        ),
      [value, active, allowLocalize, controlConfig.digits, localizedFieldValue],
    );

    const onFocusCallback = useCallback(() => {
      toggle(true);
      onFocus?.();
    }, [onFocus]);

    const onBlurCallback = useCallback(() => {
      toggle(false);
      onBlur?.();
    }, [onBlur]);

    useEffectOnce(function setDefaultValueWhenLeaveField() {
      if (!active && !value && defaultValue) {
        form.setValue(name, defaultValue, {
          shouldTouch: true,
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    });

    useEffect(
      function updateDigits() {
        updateControlConfig({ digits });
      },
      [updateControlConfig, digits],
    );

    const id = useMemo(() => v4(), []);

    const inputConfig = useMemo(() => {
      const common = {
        id,
        autoComplete: 'off',
        onFocus: onFocusCallback,
        onKeyDown,
        'data-test': `${dataTest}-input`,
        className: inputClassName,
        'data-valid': !!value,
        value: fieldValue,
        disabled,
        onChange,
      };
      if (form)
        return {
          ...common,
          ...form.register(`${name}` as const, {
            deps,
            validate,
            setValueAs: (v: string | number | undefined) =>
              v ? checkAndFormatNumericInput(v.toString(), controlConfig.digits) : v,
            onBlur: onBlurCallback,
            onChange: (event) => {
              onChange?.(
                event.target.value
                  ? checkAndFormatNumericInput(event.target.value.toString(), controlConfig.digits)
                  : event.target.value,
              );
            },
          }),
        };
      return {
        ...common,
        name,
        onBlur: onBlurCallback,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const returnString = checkAndFormatNumericInput(e.target.value, controlConfig.digits) || '';
          return onChange?.(returnString);
        },
      };
    }, [
      id,
      dataTest,
      deps,
      controlConfig.digits,
      disabled,
      fieldValue,
      form,
      inputClassName,
      name,
      onBlurCallback,
      onChange,
      onFocusCallback,
      onKeyDown,
      validate,
      value,
    ]);

    const onMinus = useCallback(() => {
      if (disabled || !Number(value) || (!stepDown && !step && !_step)) return;
      const number = decimalMinus(Number(value) || 0, stepDown ?? step ?? _step).toString();

      const update = fromExponential(!Number(number) ? '' : number);

      if (form) {
        form.setValue(name, update, {
          shouldTouch: true,
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      return onChange?.(update);
    }, [_step, disabled, form, name, onChange, step, stepDown, value]);

    const onPlus = useCallback(() => {
      if (!!max && Number(value) >= max) return;
      if (disabled || (!stepUp && !step && !_step)) return;
      const number = decimalAddition(fromExponential(Number(value)) || 0, stepUp ?? step ?? _step).toString();

      if (form) {
        form.setValue(name, number, {
          shouldTouch: true,
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      return onChange?.(number);
    }, [_step, disabled, form, max, name, onChange, step, stepUp, value]);

    useInterval(onPlus, isPlusPressed ? controlConfig.speed : null);
    useInterval(onMinus, isMinusPressed ? controlConfig.speed : null);
    useInterval(onDecrementDigits, isPlusPressed || isMinusPressed ? DIGITS_DECREMENT_INTERVAL : null);

    const onPlusDown = useCallback(() => {
      setIsPlusPressed(true);
      setIsMinusPressed(false);
      onPlus();
    }, [onPlus]);

    const onMinusDown = useCallback(() => {
      setIsPlusPressed(false);
      setIsMinusPressed(true);
      onMinus();
      onFocus?.();
    }, [onFocus, onMinus]);

    const onContextMenu = useCallback(
      (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => event.preventDefault(),
      [],
    );

    const onPlusReleased = useCallback(() => {
      setIsPlusPressed(false);
      updateControlConfig({ ...defaultControlConfig, digits, active: false });
    }, [digits, updateControlConfig]);

    const onMinusReleased = useCallback(() => {
      setIsMinusPressed(false);
      updateControlConfig({ ...defaultControlConfig, digits, active: false });
      onBlur?.();
    }, [digits, onBlur, updateControlConfig]);

    const stepUpConfig: React.HTMLAttributes<HTMLSpanElement> = useMemo(() => {
      if (isMobile) return { onTouchStart: onPlusDown, onTouchEnd: onPlusReleased };
      return {
        onMouseDown: onPlusDown,
        onMouseUp: onPlusReleased,
        onMouseLeave: onPlusReleased,
      };
    }, [isMobile, onPlusDown, onPlusReleased]);

    const stepDownConfig: React.HTMLAttributes<HTMLSpanElement> = useMemo(() => {
      if (isMobile) return { onTouchStart: onMinusDown, onTouchEnd: onMinusReleased };
      return {
        onMouseDown: onMinusDown,
        onMouseUp: onMinusReleased,
        onMouseLeave: onMinusReleased,
      };
    }, [isMobile, onMinusDown, onMinusReleased]);

    const isStepDownDisabled = useMemo(() => !Number(value), [value]);

    const renderStepDown = useCallback(() => {
      if (isStepField) {
        return (
          <span
            data-test='orders-field-exchange-minus'
            className='field-localized__control field-localized__control-minus'
            aria-disabled={isStepDownDisabled}
            onContextMenu={onContextMenu}
            {...stepDownConfig}
          >
            <SvgIconUrl className='field-localized__control-icon' symbol={svgIcons.Minus()} />
          </span>
        );
      }
      return null;
    }, [isStepDownDisabled, isStepField, onContextMenu, stepDownConfig]);

    const isStepUpDisabled = useMemo(() => {
      if (max) return Number(value) >= Number(max);

      return false;
    }, [max, value]);

    const renderStepUp = useCallback(() => {
      if (isStepField) {
        return (
          <span
            data-test='orders-field-exchange-rate-plus'
            className='field-localized__control field-localized__control-plus'
            aria-disabled={isStepUpDisabled}
            onContextMenu={onContextMenu}
            {...stepUpConfig}
          >
            <SvgIconUrl className='field-localized__control-icon' symbol={svgIcons.Plus()} />
          </span>
        );
      }
      return null;
    }, [isStepField, isStepUpDisabled, onContextMenu, stepUpConfig]);

    const renderFieldOption = useCallback(() => {
      if (!options) return null;

      return (
        <div className='field-localized__option' data-test={`${dataTest}-option`}>
          {options}
        </div>
      );
    }, [dataTest, options]);

    const renderCurrency = useCallback(() => {
      if (!currency) return null;
      if (typeof currency !== 'string') return currency;

      return (
        <span className='field-localized__currency' data-test={`${dataTest}-currency`}>
          <SvgIconUrl
            className='field-localized__currency-icon'
            dataAttribute={`${dataTest}-currency-icon`}
            symbol={svgIcons.Asset(currency)}
          />
          <p className='field-localized__currency-text' data-test={`${dataTest}-currency-label`}>
            {currency}
          </p>
        </span>
      );
    }, [currency, dataTest]);

    const renderError = useCallback(() => {
      if (!error) return null;

      return (
        <span className='field-localized__error' data-test={`${dataTest}-error`}>
          {error}
        </span>
      );
    }, [dataTest, error]);

    return (
      <div ref={ref} className={fieldClassName} data-test={dataTest}>
        <input {...inputConfig} inputMode='decimal' autoFocus={autoFocus} />
        {renderFieldOption()}
        {renderStepDown()}
        {renderStepUp()}
        <label className={labelClassName} htmlFor={id} data-test={`${dataTest}-label`}>
          {label}
        </label>
        {renderCurrency()}
        {renderError()}
      </div>
    );
  },
);

export const LocalizedNumberInput = React.memo(_LocalizedNumberInput);
